<script module lang="ts">
    import type { RowData } from "@tanstack/table-core";
    declare module "@tanstack/table-core" {
        interface ColumnMeta<TData extends RowData, TValue> {
            type: "tag" | "text" | "date" | "array";
            options?: { key: string; title: string }[];
        }
    }

    export const route = "/workbench";

    const caseStatuses = CaseStatusSchema.options
        .map((x) => ({
            key: x,
            title: m[`workbench.caseStatus.${x}`](),
        }))
        .sort((a, b) => a.title.localeCompare(b.title));

    type CaseStatus = Res["Get Workbench view"]["cases"][number]["caseStatus"];
    type CaseClassification = Res["Get Workbench view"]["cases"][number]["caseClassification"];
    const pageSizeOptions = [10, 25, 50, 100] as const;
    const defaultPageSize = 25;
</script>

<script lang="ts">
    import DataTable from "../lib/components/data-table.svelte";
    import Date from "../lib/components/common/date.svelte";
    import { renderComponent, renderSnippet } from "../lib/components/ui/data-table";
    import { createColumnHelper } from "@tanstack/table-core";
    import { cn, getClient, getHighlightSegments, goto } from "../lib/utils.js";
    import { useURL } from "../lib/url.svelte.js";
    import { useLocalStorage } from "../lib/local-storage.svelte.js";
    import type { Res } from "@repo/backend/client";
    import { Badge } from "../lib/components/ui/badge";
    import { m } from "../lib/messages.svelte.js";
    import TablePagination from "../lib/components/common/table-pagination.svelte";
    import ConditionView from "../lib/components/condition.svelte";
    import {
        base64urlCodec,
        ConditionSchema,
        definedOrDie,
        jsonCodec,
        keyOrDie,
        NumberAsStringSchema,
        z,
        type Condition,
    } from "@repo/utils";
    import { CaseStatusSchema } from "@repo/backend/z";
    import Truncated from "../lib/components/common/truncated.svelte";
    import { Input } from "../lib/components/ui/input";
    import ExternalLink from "@lucide/svelte/icons/external-link";
    import Bot from "@lucide/svelte/icons/bot";
    import Sidebar from "../lib/components/sidebar/sidebar.svelte";
    import { useOrganization, useSession } from "../lib/auth.svelte.js";
    import { resolve } from "../lib/route.svelte.js";
    import { setBreadcrumbs } from "../lib/components/breadcrumbs.svelte";
    import CaseMetadataCell from "../lib/components/case-metadata-cell.svelte";

    $effect.pre(() => {
        setBreadcrumbs([{ title: m["workbench_"](), href: resolve("/workbench") }]);
    });

    const url = useURL({
        filter: base64urlCodec(jsonCodec(ConditionSchema)),
        search: z.string(),
        pageSize: NumberAsStringSchema,
        page: z.codec(z.string(), z.number(), { encode: String, decode: (x) => Math.max(1, Math.trunc(Number(x))) }),
        view: z.string(),
    });
    const ls = useLocalStorage("workbench", {
        filter: ConditionSchema,
        search: z.string(),
        pageSize: z.number(),
    });
    let searchInput = $state(url.query.search ?? ls.search ?? "");
    let searchTimeout = $state<number>();
    const session = useSession();
    const page = $derived(url.query.page ?? 1);
    const search = $derived(url.query.search ?? ls.search ?? "");
    const pageSize = $derived(url.query.pageSize ?? ls.pageSize ?? defaultPageSize);
    let filter = $state((url.query.filter as Condition) ?? ls.filter ?? ({ op: "and", value: [] } as const));
    $effect(() => {
        const f = filter.op === "and" && filter.value.length === 0 ? undefined : filter;
        url.query.filter = f;
        ls.filter = f;
        url.query.page = undefined;
    });

    const viewFilters: Record<string, Condition | null> = $derived({
        all: null,
        so: {
            op: "and",
            value: [
                { op: "ne", field: "caseStatus", value: "NONE" },
                { op: "eq", field: "caseType", value: "SO" },
            ],
        },
        oc: {
            op: "and",
            value: [
                { op: "ne", field: "caseStatus", value: "NONE" },
                { op: "eq", field: "caseType", value: "OC" },
            ],
        },
        my: { op: "contains", field: "assignee", value: session.user.email },
    });
    const viewFilter: Condition | null = $derived(
        url.query.view && viewFilters[url.query.view]
            ? keyOrDie(viewFilters, url.query.view)
            : {
                  op: "and",
                  value: [{ op: "ne", field: "caseStatus", value: "NONE" }],
              },
    );

    let data = $state<Res["Get Workbench view"]>();
    async function fetchView(
        search: string,
        pageSize: number,
        filter: Condition,
        viewFilter: Condition | null,
        page: number,
    ) {
        return (data = await getClient()["Get Workbench view"]({
            jsonBody: {
                page,
                pageSize,
                filter: { op: "and", value: viewFilter ? [filter, viewFilter] : [filter] },
                searchQuery: search || undefined,
            },
        }));
    }
    $effect(() => {
        fetchView(search, pageSize, filter, viewFilter, page);
        const id = setInterval(() => {
            fetchView(search, pageSize, filter, viewFilter, page);
        }, 60_000);
        return () => {
            clearInterval(id);
        };
    });

    const organization = useOrganization();
    const isSO = $derived(organization.metadata["useCase"] === "SO");

    function handleMetadataChanged() {
        fetchView(search, pageSize, filter, viewFilter, page);
    }
    const c = createColumnHelper<Res["Get Workbench view"]["cases"][number]>();
    const columns = $derived([
        c.display({
            id: "caseMetadata",
            header: "",
            cell: ({ row }) => {
                return renderComponent(CaseMetadataCell, {
                    caseId: row.original.caseId,
                    tags: data?.allTags ?? [],
                    tagIds: row.original.tagIds,
                    members: organization.members,
                    ownerUserIds: row.original.ownerUserIds,
                    comment: row.original.comment,
                    onChanged: handleMetadataChanged,
                });
            },
        }),
        c.display({
            id: "openInNewTab",
            header: "",
            cell: ({ row }) => {
                return renderSnippet(openInNewTabCell, {
                    href: resolve("/workbench/:caseId", { caseId: row.original.caseId }),
                });
            },
        }),
        c.accessor("caseStatus", {
            header: m["status"](),
            cell: ({ getValue }) => {
                return renderSnippet(caseStatusCell, { value: getValue() });
            },
            meta: {
                type: "tag",
                options: caseStatuses,
            },
        }),
        ...(!isSO || organization.slug === "NOELLE"
            ? [
                  c.accessor("assignee", {
                      header: m["assignee"](),
                      cell: ({ getValue }) => {
                          return renderComponent(Truncated, {
                              value: getValue()?.join(", ") ?? null,
                              class: "w-64",
                          });
                      },
                      meta: {
                          type: "array" as const,
                      },
                  }),
              ]
            : []),
        c.accessor("emailReceivedAt", {
            header: m["received"](),
            cell: ({ getValue }) => {
                return renderComponent(Date, { value: getValue(), withTime: true });
            },
            meta: {
                type: "date",
            },
        }),
        c.accessor("emailSubject", {
            header: m["subject"](),
            cell: ({ getValue }) => {
                return renderSnippet(highlightedTextCell, { value: getValue(), class: "w-64" });
            },
            meta: {
                type: "text",
            },
        }),
        c.accessor("emailFrom", {
            header: m["from"](),
            cell: ({ getValue }) => {
                return renderSnippet(highlightedTextCell, { value: getValue(), class: "w-64" });
            },
            meta: {
                type: "text",
            },
        }),
        c.accessor("inboxId", {
            header: m["to"](),
            cell: ({ getValue }) => {
                const email = data?.inboxes.find((x) => x.id === getValue())?.email ?? null;
                return renderSnippet(highlightedTextCell, { value: email, class: "w-64" });
            },
            meta: {
                type: "tag",
                options:
                    data?.inboxes
                        .map((x) => ({ key: x.id, title: x.email }))
                        .sort((a, b) => a.title.localeCompare(b.title)) ?? [],
            },
        }),
        c.accessor("caseClassification", {
            header: m["type"](),
            cell: ({ getValue, row }) => {
                return renderSnippet(caseTypeCell, {
                    value: row.original.caseStatus === "NONE" ? null : getValue(),
                });
            },
            meta: {
                type: "tag",
                options: (
                    [
                        "ORDER",
                        "REQUEST_FOR_QUOTATION",
                        "REQUEST_FOR_PROPOSAL",
                        "QUESTION_ABOUT_ONGOING",
                        "CALL_OFF_ORDER",
                        "ADJUST_ALREADY_PLACED_ORDER",
                        "OC",
                        "OTHER",
                    ] as const
                ).map((x) => ({ key: x, title: m[`caseClassification.${x}`]() })),
            },
        }),
        c.accessor("caseReference", {
            header: m["reference"](),
            cell: ({ getValue }) => {
                return renderSnippet(highlightedTextCell, { value: getValue(), class: "w-40" });
            },
            meta: {
                type: "text",
            },
        }),
        c.accessor("partnerName", {
            header: m["partner"](),
            cell: ({ getValue }) => {
                return renderSnippet(highlightedTextCell, { value: getValue(), class: "w-48" });
            },
            meta: {
                type: "text",
            },
        }),
        ...(isSO
            ? [
                  c.accessor("erpTransactionId", {
                      header: "ERP ID",
                      cell: ({ getValue }) => {
                          return renderSnippet(highlightedTextCell, { value: getValue(), class: "w-32" });
                      },
                      meta: {
                          type: "text" as const,
                      },
                  }),
                  c.accessor("erpTransactionNumber", {
                      header: "ERP #",
                      cell: ({ getValue }) => {
                          return renderSnippet(highlightedTextCell, { value: getValue(), class: "w-32" });
                      },
                      meta: {
                          type: "text" as const,
                      },
                  }),
              ]
            : []),
    ]);
</script>

<Sidebar>
    <div class="flex items-center gap-4 border-b p-2">
        <Input
            type="search"
            placeholder={m["workbench.searchPlaceholder"]()}
            class="w-64"
            bind:value={
                () => searchInput ?? "",
                (x) => {
                    searchInput = x;
                    window.clearTimeout(searchTimeout);
                    searchTimeout = window.setTimeout(() => {
                        x = x.trim();
                        ls.search = x || undefined;
                        url.query.search = x || undefined;
                        url.query.page = undefined;
                    }, 400);
                }
            }
        />
        <ConditionView
            bind:value={filter}
            config={{
                fields: columns
                    .filter(
                        (x): x is typeof x & { accessorKey: string } =>
                            "accessorKey" in x && !!x.accessorKey && !!x.meta,
                    )
                    .map((x) => {
                        const meta = definedOrDie(x.meta);
                        return {
                            key: x.accessorKey,
                            title: typeof x.header === "string" ? x.header : x.accessorKey,
                            ...(meta.type === "tag"
                                ? { type: "options", options: definedOrDie(meta.options) }
                                : { type: meta.type }),
                        };
                    }),
            }}
            class="flex-1"
        />
    </div>
    <DataTable
        data={data?.cases ?? []}
        {columns}
        bodyProps={{
            class: "cursor-default",
        }}
        rowProps={(row) => ({
            class: "group/row",
            onclick: () => {
                goto({ route: "/workbench/:caseId", replace: false, params: { caseId: row.original.caseId } });
            },
        })}
    />
    <TablePagination
        {pageSizeOptions}
        {pageSize}
        pageNumber={page}
        totalCount={data?.totalCases ?? 0}
        onPageSizeChange={(size) => {
            const x = size === defaultPageSize ? undefined : size;
            ls.pageSize = x;
            url.query.pageSize = x;
            url.query.page = undefined;
        }}
        onPageChange={(p) => {
            url.query.page = p;
        }}
    />
</Sidebar>

{#snippet openInNewTabCell({ href }: { href: string })}
    <a
        {href}
        target="_blank"
        rel="noopener noreferrer"
        class="text-muted-foreground hover:text-foreground invisible flex items-center justify-center opacity-0 transition-opacity group-hover/row:visible group-hover/row:opacity-100"
        onclick={(e) => e.stopPropagation()}
    >
        <ExternalLink class="size-4" />
    </a>
{/snippet}

{#snippet highlightedTextCell({ value, class: className }: { value: string | null | undefined; class?: string })}
    <span class={cn("block truncate", className)} title={value ?? ""}>
        {#each getHighlightSegments(value, search) as segment (segment.index)}
            {#if segment.highlight}
                <mark class="bg-yellow-200 dark:bg-yellow-800">{segment.text}</mark>
            {:else}
                {segment.text}
            {/if}
        {/each}
    </span>
{/snippet}

{#snippet caseStatusCell({ value }: { value: Res["Get Workbench view"]["cases"][number]["caseStatus"] })}
    <Badge
        style={`--primary: var(${
            (
                {
                    PROCESSING: "--color-muted-foreground",
                    ERROR: "--color-error-foreground",
                    NONE: "--color-muted-foreground",
                    ACCEPTED: "--color-success-foreground",
                    ACCEPTED_AUTO: "--color-success-foreground",
                    ACCEPTED_EDITED: "--color-success-foreground",
                    ACCEPTED_TOLERANCE: "--color-success-foreground",
                    SUBMITTED: "--color-success-foreground",
                    SUBMITTED_AUTO: "--color-success-foreground",
                    REVIEW: "--color-warning",
                    CANCELLED: "--color-error",
                    APPROVAL: "--color-warning-foreground",
                    REJECTED: "--color-error",
                    ERP_ERROR: "--color-error-foreground",
                } satisfies Record<CaseStatus, string>
            )[value]
        })`}
        class={cn("inline-flex items-center gap-1", {
            "text-foreground": value === "REVIEW" || value === "REJECTED" || value === "CANCELLED",
        })}
    >
        {#if value === "SUBMITTED_AUTO"}
            <Bot class="size-3" />
        {/if}
        {m[`workbench.caseStatus.${value}`]()}
    </Badge>
{/snippet}

{#snippet caseTypeCell({ value }: { value: CaseClassification | null })}
    <Badge style={value === "OTHER" || !value ? "--primary: var(--color-muted-foreground)" : undefined}>
        {value ? m[`caseClassification.${value}`]() : "-"}
    </Badge>
{/snippet}
